import { useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Loader2, Phone, PhoneIncoming, Smartphone } from 'lucide-react';
import { useVenue } from '@core/contexts/VenueContext';
import { Button } from '@core/components/Button';
import { Skeleton } from '@core/components/Skeleton';
import { PhoneCodeForm } from '@core/components/PhoneCodeForm';
import { phoneErrorMessage } from '@core/utils/phone';
import { useTelegram } from '@/contexts/TelegramContext';
import { linkTelegramAccount, type TelegramLinkProof } from '@/api/telegramAuth';

/** Сообщение после входа, если Telegram дописан к карточке из браузера. */
export const LINKED_MESSAGE = 'Ваш баланс из приложения подтянут';

type Pending = 'contact' | 'skip' | null;

/**
 * Первый вход в мини-апп «сначала номер»: карточки с этим Telegram ID в заведении нет — до основного
 * приложения спрашиваем номер. Если человек уже пользовался программой через браузер (PWA), сервер
 * дописывает Telegram ID к его карточке: баланс и история на месте. «Пропустить» — новая карточка без
 * номера, как раньше. Правовые страницы открыты всегда (ссылка на политику — с этого экрана).
 */
export function PhoneFirstGate({ children }: { children: ReactNode }) {
  const { phoneRequired } = useTelegram();
  const { venue, loading, error } = useVenue();
  const location = useLocation();

  if (!phoneRequired || error || location.pathname.startsWith('/legal/')) {
    return <>{children}</>;
  }

  if (loading) {
    return (
      <div className="p-4 space-y-4">
        <Skeleton className="h-32" />
        <Skeleton className="h-24" />
      </div>
    );
  }

  return <PhoneFirstScreen venueName={venue?.name ?? null} />;
}

function PhoneFirstScreen({ venueName }: { venueName: string | null }) {
  const { initData, completePhoneStep } = useTelegram();
  const [mode, setMode] = useState<'choose' | 'manual'>('choose');
  const [pending, setPending] = useState<Pending>(null);
  const [error, setError] = useState<string | null>(null);

  const webApp = window.Telegram?.WebApp;
  const canShareContact = typeof webApp?.requestContact === 'function';

  /** Сессия по доказанному номеру (или пропуску); ошибки — наверх, их показывает вызывающий. */
  const link = async (proof: TelegramLinkProof) => {
    const data = webApp?.initData || initData;
    if (!data) {
      throw new Error('initData is missing');
    }
    const result = await linkTelegramAccount(data, proof);
    if (result.linked || result.merged) {
      toast.success(LINKED_MESSAGE, { duration: 6000 });
    }
    completePhoneStep();
  };

  const run = async (kind: Exclude<Pending, null>, proof: TelegramLinkProof) => {
    setPending(kind);
    setError(null);
    try {
      await link(proof);
    } catch (err) {
      console.warn('[PhoneFirstGate] link failed', err);
      setError(phoneErrorMessage(err));
      setPending(null);
    }
  };

  const shareContact = () => {
    if (!webApp || !canShareContact) return;
    setError(null);
    webApp.requestContact((success, event) => {
      if (!success) {
        return; // человек закрыл окно Telegram — остаёмся на экране
      }
      const contact = event?.response;
      if (!contact) {
        setError('Telegram не передал номер. Введите его вручную — мы позвоним с кодом.');
        setMode('manual');
        return;
      }
      void run('contact', { contact });
    });
  };

  const title = venueName
    ? `Уже пользуетесь «${venueName}» через браузер?`
    : 'Уже пользуетесь программой лояльности через браузер?';
  const busy = pending !== null;

  return (
    <div className="min-h-screen bg-page text-text-main flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-card rounded-brand shadow-lg p-6 space-y-5">
        <div className="text-center space-y-3">
          <div className="w-14 h-14 mx-auto rounded-full bg-primary/15 flex items-center justify-center">
            <Smartphone className="w-7 h-7 text-primary" />
          </div>
          <h1 className="text-xl font-bold text-text-main">{mode === 'manual' ? 'Подтвердите номер' : title}</h1>
          <p className="text-sm text-text-muted">
            {mode === 'manual'
              ? 'Найдём вашу карточку по номеру — баланс и история подтянутся.'
              : 'Подтвердите номер — баланс и история подтянутся.'}
          </p>
        </div>

        {error && (
          <p className="text-sm text-error bg-error/10 border border-error/20 rounded-brand px-3 py-2">{error}</p>
        )}

        {mode === 'choose' ? (
          <div className="space-y-3">
            {canShareContact && (
              <Button fullWidth onClick={shareContact} disabled={busy}>
                <span className="inline-flex items-center justify-center gap-2">
                  {pending === 'contact' ? <Loader2 size={18} className="animate-spin" /> : <Phone size={18} />}
                  {pending === 'contact' ? 'Проверяем…' : 'Поделиться номером'}
                </span>
              </Button>
            )}
            <Button
              fullWidth
              variant={canShareContact ? 'secondary' : 'primary'}
              onClick={() => {
                setError(null);
                setMode('manual');
              }}
              disabled={busy}
            >
              <span className="inline-flex items-center justify-center gap-2">
                <PhoneIncoming size={18} />
                Ввести номер
              </span>
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <PhoneCodeForm onConfirm={(phone, code) => link({ phone, code })} />
            <button
              type="button"
              onClick={() => {
                setError(null);
                setMode('choose');
              }}
              className="w-full text-sm text-text-muted underline"
            >
              Назад
            </button>
          </div>
        )}

        <button
          type="button"
          onClick={() => void run('skip', { skip: true })}
          disabled={busy}
          className="w-full inline-flex items-center justify-center gap-2 text-sm text-text-muted py-2 disabled:opacity-50"
        >
          {pending === 'skip' && <Loader2 size={16} className="animate-spin" />}
          Пропустить
        </button>

        <p className="text-xs text-text-muted text-center">
          Номер нужен, чтобы найти вашу карточку и узнавать вас на кассе.{' '}
          <Link to="/legal/privacy-policy" className="text-primary underline">
            Политика конфиденциальности
          </Link>
        </p>
      </div>
    </div>
  );
}
